import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ScanCTA } from "@/components/ScanCTA";
import { Plus, X, ScanLine, ClipboardList, Clipboard, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { parseChipTokens, parseChipWeightList, summarizeParse, readClipboardText } from "@/lib/chipParse";

/**
 * Lista de chips para operaciones masivas (ventas / traslados).
 * variant: "indiv" (chip + peso) · "mass" (pegar lista) · "both" (ambos modos).
 * total:   "count" (cuenta animales) · "weight" (cuenta + suma de pesos).
 * El pegado acepta separadores flexibles (saltos de línea, comas, punto y
 * coma, tabulaciones, espacios), numeración/viñetas y — cuando hay peso —
 * el formato "chip,peso" o "chip peso" por línea.
 */
export function ChipListField({ field, value = [], onChange }) {
  const indiv = field.variant === "indiv";
  const both = field.variant === "both";
  const withWeight = field.total === "weight";
  const [mode, setMode] = useState(field.variant === "mass" ? "mass" : "ind");
  const [chip, setChip] = useState("");
  const [weight, setWeight] = useState("");
  const [mass, setMass] = useState("");
  const [summary, setSummary] = useState(null);

  const list = Array.isArray(value) ? value : [];
  const totalWeight = list.reduce((a, r) => a + (Number(r.weight_kg) || 0), 0);

  const push = (rows) => {
    const existing = new Set(list.map((r) => r.chip_code));
    const add = rows.filter((r) => r.chip_code && !existing.has(r.chip_code));
    if (add.length) onChange(field.name, [...list, ...add]);
  };

  const addOne = () => {
    const code = chip.trim();
    if (!code) return;
    push([{ chip_code: code, weight_kg: indiv && weight ? Number(weight) : undefined }]);
    setChip(""); setWeight("");
  };

  const runParse = (text) => (withWeight ? parseChipWeightList(text) : parseChipTokens(text));

  const addMass = (text) => {
    const raw = text ?? mass;
    if (!raw.trim()) { toast.error("Pegá o escribí una lista de chips"); return; }
    const parsed = runParse(raw);
    const rows = withWeight ? parsed.valid : parsed.valid.map((c) => ({ chip_code: c }));
    push(rows);
    setSummary(summarizeParse(parsed) + (parsed.invalid.length ? `: ${parsed.invalid.slice(0, 5).join(", ")}${parsed.invalid.length > 5 ? "…" : ""}` : ""));
    setMass("");
  };

  const pasteFromClipboard = async () => {
    const text = await readClipboardText();
    if (text === null) {
      toast.error("No pude leer el portapapeles — pegá manualmente en el cuadro de texto (mantené presionado y elegí Pegar)");
      return;
    }
    if (!text.trim()) { toast.info("El portapapeles está vacío"); return; }
    addMass(text);
  };

  const removeAt = (i) => onChange(field.name, list.filter((_, k) => k !== i));

  return (
    <div className="flex flex-col gap-2" data-testid={`chips-${field.name}`}>
      <label className="text-[13.5px] font-heading font-semibold text-foreground">{field.lab}</label>
      <div className="rounded-[var(--radius)] border-2 border-border bg-card overflow-hidden shadow-paper">
        <div className="p-4 space-y-3">
          {both && (
            <div className="grid grid-cols-2 gap-2.5">
              <button type="button" onClick={() => setMode("ind")} data-testid={`chips-${field.name}-mode-ind`}
                className={cn("inline-flex items-center justify-center gap-1.5 h-11 rounded-[calc(var(--radius)-0.4rem)] text-[13px] font-heading font-semibold border-2 transition-colors",
                  mode === "ind" ? "bg-accent text-accent-foreground border-primary/40" : "bg-muted/40 text-muted-foreground border-transparent")}>
                <ScanLine size={15} /> Individual
              </button>
              <button type="button" onClick={() => setMode("mass")} data-testid={`chips-${field.name}-mode-mass`}
                className={cn("inline-flex items-center justify-center gap-1.5 h-11 rounded-[calc(var(--radius)-0.4rem)] text-[13px] font-heading font-semibold border-2 transition-colors",
                  mode === "mass" ? "bg-accent text-accent-foreground border-primary/40" : "bg-muted/40 text-muted-foreground border-transparent")}>
                <ClipboardList size={15} /> Carga masiva
              </button>
            </div>
          )}

          {mode === "ind" ? (
            <div className="space-y-2.5">
              <div className="flex gap-2.5">
                <Input data-testid={`chips-${field.name}-input`} value={chip}
                  onChange={(e) => setChip(e.target.value.replace(/\s/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOne(); } }}
                  placeholder="Escanear o escribir chip…" className="h-12 font-mono flex-1" />
                {indiv && (
                  <Input data-testid={`chips-${field.name}-weight`} type="number" inputMode="decimal" value={weight}
                    onChange={(e) => setWeight(e.target.value)} placeholder="kg" className="h-12 w-[84px]" />
                )}
                <Button type="button" onClick={addOne} className="h-12 w-12 p-0 shrink-0" data-testid={`chips-${field.name}-add`}>
                  <Plus size={20} />
                </Button>
              </div>
              <ScanCTA testId={`chips-${field.name}-scan`} label="Escanear chip" fullWidth
                onChip={(c) => { if (indiv) setChip(c); else push([{ chip_code: c }]); }} />
            </div>
          ) : (
            <div className="space-y-2.5">
              <Textarea data-testid={`chips-${field.name}-textarea`} value={mass}
                onChange={(e) => { setMass(e.target.value); setSummary(null); }} rows={4}
                placeholder={withWeight ? "Pegá la lista: un chip por línea, con peso opcional (chip,peso)…" : "Pegá varios chips — uno por línea, separados por coma, etc…"}
                className="font-mono text-[13px]" />
              <div className="grid grid-cols-2 gap-2.5">
                <Button type="button" onClick={pasteFromClipboard} variant="outline" className="h-12" data-testid={`chips-${field.name}-paste-clipboard`}>
                  <Clipboard size={16} /> Pegar del portapapeles
                </Button>
                <Button type="button" onClick={() => addMass()} variant="outline" className="h-12" data-testid={`chips-${field.name}-add-mass`}>
                  <Plus size={17} /> Cargar lista
                </Button>
              </div>
              {summary && (
                <p className="text-[12.5px] text-muted-foreground flex items-start gap-1.5 leading-relaxed" data-testid={`chips-${field.name}-summary`}>
                  <AlertTriangle size={13} className="shrink-0 mt-0.5 text-[hsl(var(--state-unknown))]" />{summary}
                </p>
              )}
            </div>
          )}
        </div>

        {list.length > 0 && (
          <div className="border-t-2 border-border divide-y divide-border" data-testid={`chips-${field.name}-list`}>
            {list.map((r, i) => (
              <div key={`${r.chip_code}-${i}`} className="flex items-center gap-3 px-4 py-2.5">
                <span className="h-7 w-7 rounded-md bg-primary text-primary-foreground grid place-items-center text-[11px] font-mono font-semibold shrink-0">{i + 1}</span>
                <span className="font-mono text-[14px] tabular-nums tracking-tight truncate flex-1">{r.chip_code}</span>
                {r.weight_kg != null && r.weight_kg !== "" && <span className="text-[12.5px] font-mono text-muted-foreground">{r.weight_kg} kg</span>}
                <button type="button" onClick={() => removeAt(i)} className="text-destructive hover:opacity-70 shrink-0 p-1.5" data-testid={`chips-${field.name}-del-${i}`}>
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between bg-primary text-primary-foreground px-4 py-3.5">
          <span className="text-[13px] font-heading font-semibold">
            {field.total === "weight" ? "Total animales / peso" : "Total animales"}
            <span className="ml-1.5 text-[10px] font-mono opacity-70 uppercase tracking-wide">automático</span>
          </span>
          <span className="font-mono font-bold text-[19px] tabular-nums" data-testid={`chips-${field.name}-total`}>
            {list.length}
            {field.total === "weight" && totalWeight > 0 && <span className="text-[14px] opacity-85"> · {totalWeight} kg</span>}
          </span>
        </div>
      </div>
    </div>
  );
}
